// src/components/modals/CourseScheduleModal.jsx
import React, { useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

/* ── All helpers unchanged ───────────────────────────────────── */
const DOW_LABELS = ["יום א׳","יום ב׳","יום ג׳","יום ד׳","יום ה׳","יום ו׳","שבת"];

const DAY_KEY_TO_INDEX = {
  sunday:0, monday:1, tuesday:2, wednesday:3, thursday:4, friday:5, saturday:6,
};

function toDayIndexFromAny(rawDay) {
  if (rawDay == null) return null;
  if (typeof rawDay === "number" && rawDay >= 0 && rawDay <= 6) return rawDay;
  if (typeof rawDay === "string" && /^\d+$/.test(rawDay)) {
    const n = parseInt(rawDay, 10);
    if (n >= 0 && n <= 6) return n;
  }
  if (typeof rawDay === "string") {
    const lower = rawDay.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(DAY_KEY_TO_INDEX, lower)) return DAY_KEY_TO_INDEX[lower];
  }
  return null;
}

function flattenWeeklyGroups(weekly) {
  const result = [];
  weekly.forEach((group, idx) => {
    if (!group) return;
    const groupDayIndex = toDayIndexFromAny(group.dayIndex ?? group.day ?? group.dow ?? group.weekday ?? group._day ?? idx);
    const groupDayLabel = (group.dayLabel || group.dayName || "").toString().trim();
    const slots = group.meetings || group.slots || group.items || (Array.isArray(group) ? group : []);
    if (!Array.isArray(slots)) return;
    slots.forEach((slot) => {
      const slotDayIndex = toDayIndexFromAny(slot.dayIndex ?? slot.day ?? slot.dow ?? slot.weekday ?? slot._day) ?? groupDayIndex;
      result.push({ ...slot, dayIndex: slotDayIndex, dayLabel: (slot.dayLabel || slot.dayName || groupDayLabel).toString().trim() });
    });
  });
  return result;
}

function normalizeEntry(raw, fallbackDayIndex = null) {
  if (!raw || typeof raw !== "object") return null;
  let dayIndex = toDayIndexFromAny(raw.dayIndex ?? raw.day ?? raw.dow ?? raw.weekday ?? raw._day ?? fallbackDayIndex);
  const explicitDayLabel = (raw.dayLabel || raw.dayName || "").toString().trim();
  let dayLabel = explicitDayLabel;
  if (!dayLabel && raw.day) { const idx = toDayIndexFromAny(raw.day); if (idx != null) dayLabel = DOW_LABELS[idx]; }
  if (!dayLabel && dayIndex != null) dayLabel = DOW_LABELS[dayIndex];
  if (!dayLabel) dayLabel = "יום לא ידוע";
  const startTime = raw.startTime ?? raw.start ?? raw.from ?? "";
  const endTime   = raw.endTime   ?? raw.end   ?? raw.to   ?? "";
  const lessons = raw.lessons ?? raw.count ?? raw.sessions ?? 1;
  return { ...raw, dayIndex, dayLabel, startTime: startTime || "", endTime: endTime || "", lessons: Number.isFinite(lessons) ? lessons : 1 };
}

function extractRawEntries(schedule) {
  if (!schedule) return [];
  if (Array.isArray(schedule)) return schedule.slice();
  const weekly = schedule.weekly;
  if (Array.isArray(weekly)) {
    const first = weekly[0];
    if (first && typeof first === "object" && (first.meetings || first.slots || first.items || Array.isArray(first)))
      return flattenWeeklyGroups(weekly);
    return weekly.slice();
  }
  if (weekly && typeof weekly === "object") {
    const result = [];
    Object.entries(weekly).forEach(([dayKey, slots]) => {
      if (!Array.isArray(slots)) return;
      const dayIndex = toDayIndexFromAny(dayKey);
      slots.forEach((slot) => result.push({ ...slot, dayIndex: slot.dayIndex ?? dayIndex }));
    });
    return result;
  }
  return [];
}

function groupByDay(entries) {
  const groups = {};
  for (const e of entries) {
    const key = e.dayIndex != null ? e.dayIndex : "unknown";
    if (!groups[key]) groups[key] = [];
    groups[key].push(e);
  }
  return groups;
}

/* ── Component ───────────────────────────────────────────────── */
const CourseScheduleModal = ({ isOpen, onClose, schedule, courseTitle = "קורס" }) => {
  const modalRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [isOpen, onClose]);

  const normalized = useMemo(() => {
    return extractRawEntries(schedule)
      .map((raw) => normalizeEntry(raw, raw.dayIndex))
      .filter(Boolean)
      .sort((a, b) => {
        const da = a.dayIndex ?? 10, db = b.dayIndex ?? 10;
        return da !== db ? da - db : (a.startTime || "").localeCompare(b.startTime || "");
      });
  }, [schedule]);

  const grouped    = useMemo(() => groupByDay(normalized), [normalized]);
  const hasSchedule = normalized.length > 0;

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[120000] flex items-end sm:items-center justify-center px-0 sm:px-4"
      aria-modal="true"
      role="dialog"
      onMouseDown={(e) => { if (!modalRef.current?.contains(e.target)) onClose?.(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-[3px]" onClick={onClose}/>

      {/* Modal */}
      <div
        ref={modalRef}
        dir="rtl"
        onMouseDown={(e) => e.stopPropagation()}
        className="relative z-[1] w-full sm:max-w-lg bg-white
                   rounded-t-3xl sm:rounded-3xl shadow-2xl shadow-black/20
                   flex flex-col max-h-[96dvh] sm:max-h-[85vh] overflow-hidden"
      >
        {/* ── Header ── */}
        <div className="relative flex-shrink-0 bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-gray-200 px-5 sm:px-7 py-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-md flex items-center justify-center">
              <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <rect x="3" y="4" width="18" height="18" rx="3"/>
                <line x1="3" y1="9" x2="21" y2="9"/>
                <line x1="7" y1="2" x2="7" y2="6"/>
                <line x1="17" y1="2" x2="17" y2="6"/>
              </svg>
            </div>
            <div className="min-w-0">
              <h2 className="text-gray-800 font-bold text-base sm:text-lg leading-tight">מערכת שעות לקורס</h2>
              <p className="text-gray-500 text-xs mt-0.5 truncate">{courseTitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="סגור"
            className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-white/80 hover:bg-white border border-gray-200 hover:border-gray-300 flex items-center justify-center transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 2l10 10M12 2L2 12" stroke="#6b7280" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5 space-y-3">
          {!hasSchedule ? (
            <div className="flex flex-col items-center justify-center py-14 text-center text-gray-300">
              <svg className="w-12 h-12 mb-3" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="4" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="1.5"/>
                <line x1="3" y1="9" x2="21" y2="9" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M8 2.5v4M16 2.5v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <p className="text-sm font-semibold text-gray-400 mb-1">עדיין לא נקבעה מערכת שעות</p>
              <p className="text-xs text-gray-300 max-w-[220px]">כאשר המורה יגדיר מערכת שעות, היא תופיע כאן.</p>
            </div>
          ) : (
            Object.keys(grouped)
              .sort((a, b) => (a === "unknown" ? 10 : parseInt(a,10)) - (b === "unknown" ? 10 : parseInt(b,10)))
              .map((key) => {
                const items = grouped[key];
                if (!items?.length) return null;
                const dayLabel = items[0].dayLabel || "יום לא ידוע";
                return (
                  <section key={key} className="rounded-2xl border border-gray-100 overflow-hidden">
                    {/* Day header */}
                    <div className="flex items-center justify-between px-4 py-2.5 bg-indigo-50/60 border-b border-indigo-100">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-indigo-400"/>
                        <h3 className="text-xs font-bold text-indigo-700 uppercase tracking-wide">{dayLabel}</h3>
                      </div>
                      <span className="text-[11px] text-indigo-400 font-medium">
                        {items.length} {items.length === 1 ? "מפגש" : "מפגשים"}
                      </span>
                    </div>

                    {/* Slots */}
                    <ul className="divide-y divide-gray-50 bg-white">
                      {items.map((m, idx) => (
                        <li key={idx} className="flex items-center justify-between px-4 py-3">
                          <div className="flex items-center gap-2.5 text-sm text-gray-700">
                            <svg className="w-4 h-4 text-gray-400 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6"/>
                              <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                            </svg>
                            <span className="font-semibold">{m.startTime || "—"}</span>
                            <span className="text-gray-400 text-xs">–</span>
                            <span className="font-semibold">{m.endTime || "—"}</span>
                          </div>
                          <span className="text-[11px] font-medium text-gray-400">
                            {m.lessons ?? 1} {(m.lessons ?? 1) === 1 ? "שיעור" : "שיעורים"}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex-shrink-0 border-t border-gray-100 bg-white/80 backdrop-blur-sm rounded-b-3xl px-5 sm:px-7 py-4 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="h-10 px-5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-500
                       hover:bg-gray-50 hover:border-gray-300 transition-all duration-150"
          >
            סגור
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CourseScheduleModal;